import { useState } from "react";
import { clearProposalId, clearTypeModal } from "../../features/gms-state-and-modal/modal-slice";
import { useDispatch } from "react-redux";
import { useIsSameUser } from "../proposal/use-state-checker-proposal";
import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { useToastify } from "../../library/toastify/toast-provider";
import { useApprovedProposalExtensionMutation, useDeclinedProposalExtensionMutation, useRequestProposalExtensionMutation } from "../../api/gms/proposal-extension/proposal-extension-api";
import { setClearProposalExtension } from "../../features/gms-state-and-modal/state-slice";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useExtensionActions = () => {

    const dispatch = useDispatch();
    const { showToastNotify } = useToastify();
    const { 
        isCustomize,
        isBookedProposal,
        isClosedProposal,
    } = useProposalTypeData();
    const isSameUser = useIsSameUser();

    const [approveExtension, { isLoading: isLoadingApproveExtension }] = useApprovedProposalExtensionMutation()
    const [declinedExtension, { isLoading: isLoadingDeclinedExtension }] = useDeclinedProposalExtensionMutation()
    const [requestProposalExtension, { isLoading: isLoadingRequestProposalExtension }] = useRequestProposalExtensionMutation()
    
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { currentProposalId } = useModalAccess();
    const [confirmationAction, setConfirmationAction] = useState<'accept' | 'reject' | null>(null);

    const showRequestExtension = isCustomize && isSameUser && !isBookedProposal && !isClosedProposal;

    const handleOpenConfirmation = () => {
        if (!currentProposalId || isSubmitting) return;
        setShowConfirmModal(true);
    };

    const handleCancelConfirmation = () => {
        if (isSubmitting) return;
        setShowConfirmModal(false);
    };

    const handleRequestExtension = async () => {
        if (!currentProposalId || isSubmitting) return;
        setIsSubmitting(true);

        try {
            const response: any = await requestProposalExtension(currentProposalId).unwrap();
            const message = response.message || 'Extension request submitted to your Team Leader/Admin.';
            showToastNotify(message, 'success');
            setIsRequestPending(true);
            setShowConfirmModal(false);
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || 'An unexpected error occurred';
            showToastNotify(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleActionExtension = async (type: 'reject' | 'accept') => {
        if (!currentProposalId || isSubmitting) return;

        try {
            if (type === 'accept') {
                await approveExtension(currentProposalId).unwrap();
                showToastNotify('Extension approved successfully.', 'success');
            }

            if (type === 'reject') {
                await declinedExtension(currentProposalId).unwrap();
                showToastNotify('Extension request denied.', 'success');
            }
            setConfirmationAction(null);
            dispatch(clearProposalId());
            dispatch(clearTypeModal());
            dispatch(setClearProposalExtension());
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };

    return {
        handleOpenConfirmation,
        handleCancelConfirmation,
        handleRequestExtension,
        showConfirmModal,
        isRequestPending,
        isSubmitting,
        showRequestExtension,

        confirmationAction,
        setConfirmationAction,
        handleActionExtension,

        isLoadingApproveExtension,
        isLoadingDeclinedExtension,
        isLoadingRequestProposalExtension,
    }
}